import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { LayoutList, Grid3X3, Save, Undo2, LogOut, Loader2, Sliders, ExternalLink, Users, CalendarDays, BellRing, MessageSquare } from "lucide-react";
import { AdminLogin } from "@/components/admin/AdminLogin";
import { StreamsEditor } from "@/components/admin/StreamsEditor";
import { GridsEditor } from "@/components/admin/GridsEditor";
import { BackupsPanel } from "@/components/admin/BackupsPanel";
import { CandidatesPanel } from "@/components/admin/CandidatesPanel";
import { AlertsPanel } from "@/components/admin/AlertsPanel";
import { FeedbackPanel } from "@/components/admin/FeedbackPanel";
import { DrawsEditor } from "@/components/admin/DrawsEditor";
import { adminMe, adminRules, adminSaveRules, adminRestore, buildPatch, getAdminToken, setAdminToken, apiError } from "@/lib/adminApi";

const clone = (o) => JSON.parse(JSON.stringify(o));

export default function Admin() {
  const [authed, setAuthed] = useState(null);
  const [original, setOriginal] = useState(null);
  const [draft, setDraft] = useState(null);
  const [backups, setBackups] = useState([]);
  const [tab, setTab] = useState("streams");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const data = await adminRules();
    setOriginal(data.config);
    setDraft(clone(data.config));
    setBackups(data.backups);
  }, []);

  useEffect(() => {
    if (!getAdminToken()) { setAuthed(false); return; }
    adminMe().then(() => { setAuthed(true); load(); }).catch(() => { setAdminToken(null); setAuthed(false); });
  }, [load]);

  const dirty = useMemo(() => draft && original && JSON.stringify(buildPatch(draft)) !== JSON.stringify(buildPatch(original)), [draft, original]);

  const handle = async (fn, okMsg) => {
    setBusy(true);
    try {
      const data = await fn();
      setOriginal(data.config);
      setDraft(clone(data.config));
      setBackups(data.backups);
      toast.success(okMsg);
      if (data.alerts?.sent) toast.info(`${data.alerts.sent} draw alert email${data.alerts.sent === 1 ? "" : "s"} sent to subscribers`);
      if (data.alerts?.failed) toast.error(`${data.alerts.failed} draw alert email(s) failed — see Alerts tab`);
    } catch (e) {
      if (e?.response?.status === 401) { setAdminToken(null); setAuthed(false); }
      toast.error(apiError(e, "Save failed"));
    } finally {
      setBusy(false);
    }
  };

  const save = () => handle(() => adminSaveRules(buildPatch(draft)), "Rules saved — live for all new assessments");
  const restore = (name) => window.confirm("Restore this version? Current rules will be backed up first.") && handle(() => adminRestore(name), "Previous version restored");
  const logout = () => { setAdminToken(null); setAuthed(false); setDraft(null); setOriginal(null); };

  if (authed === null) return <div className="flex justify-center py-24 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  if (!authed) return <AdminLogin onSuccess={() => { setAuthed(true); load(); }} />;
  if (!draft) return <div className="flex justify-center py-24 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>;

  return (
    <div className="mx-auto max-w-7xl px-4 py-10 pb-32 sm:px-6 lg:px-8" data-testid="admin-page">
      <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
        <div>
          <span className="inline-flex items-center gap-2 rounded-full bg-[#0F172A] px-3 py-1 text-xs font-bold uppercase tracking-wider text-white"><Sliders className="h-3.5 w-3.5" /> Admin</span>
          <h1 className="mt-3 font-heading text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">Rules editor</h1>
          <p className="mt-2 text-slate-600">
            Config version <span className="font-mono-tech font-semibold text-slate-900" data-testid="admin-version">{draft.meta.version}</span> · last updated {draft.meta.last_updated}
            {draft.meta.last_change ? <span className="text-slate-400"> · {draft.meta.last_change}</span> : null}
          </p>
        </div>
        <div className="flex gap-2 self-start">
          <Link to="/rules" className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">
            Public view <ExternalLink className="h-4 w-4" />
          </Link>
          <button onClick={logout} data-testid="admin-logout-btn" className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </div>
      </div>

      <div className="mt-8 grid gap-6 lg:grid-cols-12">
        <div className="lg:col-span-9">
          <div className="inline-flex rounded-lg border border-slate-200 bg-white p-1 shadow-sm">
            {[["streams", LayoutList, "Streams & cutoffs"], ["grids", Grid3X3, "Point grids"], ["draws", CalendarDays, "Draw rounds"], ["candidates", Users, "Candidates"], ["alerts", BellRing, "Alerts"], ["feedback", MessageSquare, "Feedback"]].map(([id, Icon, label]) => (
              <button key={id} onClick={() => setTab(id)} data-testid={`admin-tab-${id}`}
                className={`inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-semibold transition-colors ${tab === id ? "bg-[#C8102E] text-white" : "text-slate-600 hover:text-slate-900"}`}>
                <Icon className="h-4 w-4" /> {label}
              </button>
            ))}
          </div>
          <div className="mt-4">
            {tab === "streams" && <StreamsEditor streams={draft.streams} original={original.streams} onChange={(streams) => setDraft({ ...draft, streams })} />}
            {tab === "grids" && <GridsEditor grids={draft.grids} original={original.grids} onChange={(grids) => setDraft({ ...draft, grids })} />}
            {tab === "draws" && <DrawsEditor streams={draft.streams} draws={draft.draws || {}} original={original.draws || {}} onChange={(draws) => setDraft({ ...draft, draws })} />}
            {tab === "candidates" && <CandidatesPanel />}
            {tab === "alerts" && <AlertsPanel />}
            {tab === "feedback" && <FeedbackPanel />}
          </div>
        </div>
        <div className="lg:col-span-3">
          <BackupsPanel backups={backups} onRestore={restore} busy={busy} />
        </div>
      </div>

      {dirty && (
      <div className="fixed inset-x-0 bottom-0 z-40 border-t bg-white/95 backdrop-blur animate-in slide-in-from-bottom duration-300" data-testid="admin-save-bar">
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-3 px-4 py-3 sm:flex-row sm:px-6 lg:px-8">
          <p className="text-sm font-semibold text-slate-800">You have unsaved changes. Saving updates the live rules and creates a backup.</p>
          <div className="flex gap-2">
            <button onClick={() => setDraft(clone(original))} disabled={busy} data-testid="admin-discard-btn"
              className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50">
              <Undo2 className="h-4 w-4" /> Discard
            </button>
            <button onClick={save} disabled={busy} data-testid="admin-save-btn"
              className="inline-flex items-center gap-1.5 rounded-md bg-[#C8102E] px-5 py-2 text-sm font-semibold text-white hover:bg-[#A60C24] disabled:opacity-60">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save changes
            </button>
          </div>
        </div>
      </div>
      )}
    </div>
  );
}
